import { useEffect, useRef, useState } from 'react';
import "./EmployeeRecordSelect.css";
import { EmployeeRecord } from 'winslow-core';
import { useSelector } from 'react-redux';
import { selectUser } from '../../redux/features/appData';
import { apiGetEmployeeRecordsForWorkspace } from '../../services/EmployeeRecordService';
import { GenericSelectOption } from './GenericSelect';
import { erGetCPValue } from "../app/app-employee-records/employeeRecordUtils";
import { X } from 'lucide-react';

type EmployeeRecordSelectProps = {
    selected?: string;
    onChange: (value: any) => void;
    maxHeight?: string;
}

export const EmployeeRecordSelect = ({
        selected, onChange, maxHeight = "120px", ...rest 
    }: EmployeeRecordSelectProps) => {

    const storeUser = useSelector(selectUser);

    const [employeeRecords, setEmployeeRecords] = useState<EmployeeRecord[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [text, setText] = useState("");
    const [options, setOptions] = useState<GenericSelectOption[]>([]);
    const [filteredOptions, setFilteredOptions] = useState<GenericSelectOption[]>([]);

    const selectRef = useRef(null);

    useEffect(() => {
        document.addEventListener('keydown', eventKeyDown);
        document.addEventListener('mousedown', eventMouseDown);

        (async () => {  
            const theEmployeeRecords = 
                await apiGetEmployeeRecordsForWorkspace(storeUser?.currentWorkspaceId || "");
                
            if (!theEmployeeRecords) {
                console.error("No employee records found");
            } 

            setEmployeeRecords(theEmployeeRecords || []);
            const theOptions = (theEmployeeRecords || []).map((employeeRecord: EmployeeRecord) => {
                return {
                    value: employeeRecord.id,
                    displayName: `${erGetCPValue(employeeRecord, "system-firstName")} ${erGetCPValue(employeeRecord, "system-lastName")}`,
                    image: employeeRecord.avatarURL
                }
            })

            setOptions(theOptions);
        })();
                
        // get rid of the clickout event handler if needed
        return () => {
            document.removeEventListener('keydown', eventKeyDown);
            document.removeEventListener('mousedown', eventMouseDown);
        }
    }, []);

    useEffect(() => {
        const theFilteredOptions = options.filter((option) => {
            if (text === "") {
                return true;
            }

            return option.displayName.toLowerCase().includes(text.toLowerCase());
        }).slice(0,5);

        setFilteredOptions(theFilteredOptions);
    }, [text, options]);

    const eventKeyDown = (e: any) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(false);
        }
    }

    const eventMouseDown = (e: any) => {
        const current : any = selectRef.current;
        if (current && !current.contains(e.target)) {
            setIsOpen(false);
        }
    }

    const handleClick = (e: any) => {
        // if it is now going to be open
        if (!isOpen) {
            document.addEventListener('keydown', eventKeyDown, true);
        }

        setIsOpen(!isOpen);
    }

    const selectedOption = options.find((option) => option.value === selected);

    // console.log("EmployeeRecordSelect: options: ", options);
    // console.log("EmployeeRecordSelect: filteredOptions: ", filteredOptions);

    return (
        <div 
            className="employee-record-select-container"
            ref={selectRef}
        >
            <div 
                className="employee-record-select"
                onClick={handleClick}
                style={{
                    ...(isOpen ? {zIndex: 101} :{}),
                    ...(maxHeight ? {maxHeight: maxHeight} : {})
                    }}
            >
                <div 
                    className="employee-record-select-input"
                >
                    {!selectedOption &&
                        <input 
                            type="text"
                            placeholder="Enter an employee name"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                        />
                    }
                    {selectedOption &&
                        <>
                            <div className="employee-record-select-selected">
                                {selectedOption.image && <img src={selectedOption.image} />} {selectedOption.displayName}
                            </div>
                            <div 
                                className="employee-record-select-selected-delete"
                                onClick={()=> onChange("")}
                            >
                                <X size={16} />
                            </div>
                        </>
                    }
                </div>

                {isOpen &&
                    <div className="employee-record-select-options">
                        {filteredOptions.map((option: GenericSelectOption) => {
                            return (				
                                <div 
                                    key={option.value}
                                    className="employee-record-select-option"
                                    onClick={() => {
                                        onChange(option.value);
                                        setIsOpen(false);
                                    }}
                                >
                                    {option.image && 
                                        <img src={option.image} />
                                    }
                                    {option.displayName}
                                </div>
                            )
                        }
                        )}
                    </div>
                }

            </div>
        </div>
    )
}
